import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Footer from "../../GlobalComp/Footer/Footer";
import Classes from "./ContactUs.module.css";
import { useNavigate } from "react-router-dom";
import Navbar from "../Home/Components/Navbar/Navbar";
import { EnvelopIcon, PhoneIcon } from "../../../utils/Icons";
import backend from "../../../api";
import { useSelector } from "react-redux";
import SEO from "../../../seo/SEO";
import { SITE_URL } from "../../../seo/config";

export default function ContactUs() {
  const navigateTo = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [formdata, setFormdata] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "", // ✅ NEW
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" }); // '', 'success', 'error'

  // Prefill when user is logged in
  useEffect(() => {
    if (!user) return;
    setFormdata((prev) => ({
      firstName:
        prev.firstName ||
        user.firstName ||
        (user.name ? String(user.name).split(" ")[0] : "") ||
        "",
      lastName:
        prev.lastName ||
        user.lastName ||
        (user.name ? String(user.name).split(" ").slice(1).join(" ") : "") ||
        "",
      email: prev.email || user.email || "",
      phone: prev.phone || user.phone || user.phoneNumber || "", // ✅ NEW (safe prefill)
      message: prev.message || "",
    }));
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormdata((p) => ({ ...p, [name]: value }));
    if (status.type) setStatus({ type: "", msg: "" });
  };

  const isValidEmail = (v) => /^\S+@\S+\.\S+$/.test(String(v || "").trim());

  // ✅ simple phone validation: optional, but if entered must be >= 7 digits
  const isValidPhone = (v) => {
    const s = String(v || "").trim();
    if (!s) return true; // allow empty if you want it optional
    const digits = s.replace(/[^\d]/g, "");
    return digits.length >= 7;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, email, phone, message } = formdata;

    if (
      !String(firstName).trim() ||
      !String(lastName).trim() ||
      !String(email).trim() ||
      !String(message).trim()
    ) {
      setStatus({ type: "error", msg: "Please fill in all fields." });
      return;
    }
    if (!isValidEmail(email)) {
      setStatus({ type: "error", msg: "Please enter a valid email address." });
      return;
    }
    if (!isValidPhone(phone)) {
      setStatus({ type: "error", msg: "Please enter a valid phone number." });
      return;
    }

    // ✅ concatenate phone with message (without changing backend integration)
    const phoneLine = String(phone || "").trim()
      ? `Phone: ${String(phone).trim()}\n\n`
      : "";

    const body = {
      fullname: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim(),
      message: `${phoneLine}${message.trim()}`, // ✅ NEW
    };

    try {
      setSubmitting(true);
      setStatus({ type: "", msg: "" });

      const res = await backend.post("/contact", body);

      if (res?.status === 200) {
        setStatus({
          type: "success",
          msg: "Message sent successfully. We’ll get back to you soon!",
        });
        setFormdata({
          firstName: "",
          lastName: "",
          email: user?.email || "",
          phone: user?.phone || user?.phoneNumber || "", // ✅ keep prefill
          message: "",
        });
      } else {
        setStatus({
          type: "error",
          msg: "Could not send your message. Please try again.",
        });
      }
    } catch (err) {
      console.error("Contact submit error:", err);
      setStatus({
        type: "error",
        msg: err?.response?.data?.error || "Something went wrong while sending the email.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const Input = (props) => (
    <input {...props} className={Classes.inputField} disabled={submitting} />
  );

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: `${SITE_URL}/contact-us`,
    about: {
      "@type": "Organization",
      name: "Fuel Clubs",
    },
  };

  return (
    <>
      <SEO
        title="Contact Fuel Clubs | We’re Here to Help"
        description="Questions about memberships, scheduling, or fleet pricing? Contact Fuel Clubs and our team will get back to you."
        keywords={[
          "contact Fuel Clubs",
          "fuel delivery support",
          "fleet fueling contact",
          "Las Vegas fuel delivery help",
        ]}
        robots="index, follow"
        canonical="/contact-us"
        image="/logo.png"
        type="website"
        structuredData={contactSchema}
      />

      <div className={Classes.hero}>
        <Navbar isShadow={true} />

        <div className={Classes.heroContent}>
          <div className={Classes.leftContainer}>
            <div>
              <h1 className={Classes.subheading}>Contact Us</h1>
              <p className={Classes.text}>
                Join Fuel Clubs and enjoy effortless
                <br />
                scheduling, real-time tracking, and fuel
                <br />
                brought directly to you.
              </p>

              <div className={Classes.flexAlign10}>
                <EnvelopIcon height="22" fill="var(--primary-color)" />
                <h5 className={Classes.text} style={{ margin: 0 }}>
                  Contact@fuelclubs.com
                </h5>
              </div>

              <div className={Classes.flexAlign10}>
                <PhoneIcon height="22" fill="var(--primary-color)" />
                <h5 className={Classes.text} style={{ margin: 0 }}>
                  (702) 846-0267
                </h5>
              </div>
            </div>
          </div>

          <div className={Classes.RightContainer}>
            <motion.div
              className={Classes.formContainer}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <form onSubmit={handleSubmit} noValidate>
                <div className={Classes.twoElem}>
                  <Input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formdata.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                  />
                  <Input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={formdata.lastName}
                    onChange={handleChange}
                    required
                    autoComplete="family-name"
                  />
                </div>

                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formdata.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />

                {/* ✅ NEW: Phone field */}
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formdata.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  inputMode="tel"
                />

                <textarea
                  name="message"
                  className={Classes.textarea || Classes.inputField}
                  style={{ height: "auto" }}
                  placeholder="Message..."
                  rows={7}
                  value={formdata.message}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                  spellCheck={false}
                />

                {status.type && (
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontSize: 13,
                      color: status.type === "error" ? "#b91c1c" : "#166534",
                    }}
                    role="status"
                    aria-live="polite"
                  >
                    {status.msg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  aria-disabled={submitting}
                  className={Classes.submitBtn}
                  style={{
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                >
                  <h5>{submitting ? "Sending…" : "Send Message"}</h5>
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
